import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation, useApolloClient, useQuery } from '@apollo/client/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
  Button,
} from '@scio3/ui';
import {
  START_TRIAL_ADDON,
  UPDATE_SUBSCRIPTION_ADDON,
  GET_COMPANY_INFO,
  GET_PRICE_AUTO_CONVERT_CURRENCIES,
} from '@scio3/graphql-client';
import type { CurrencyOptionEntry } from '../utils/readAddonAmounts';
import { readAddonAmounts } from '../utils/readAddonAmounts';
import { DEVICE_CODE_STATUS, PLAN_FEATURES } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { useBillingDialogStore, openUpgradeWizard } from '../stores/billing-dialog-store';
import { useBillingData } from '../hooks/useBillingData';
import { extractMutationResponse } from '../utils/plan-helpers';
import {
  ADDON_DISPLAY_NAMES,
  ADDON_FLAT_PACKAGE,
  ADDON_TIER_CATALOG,
  OPTIDEV_BASE_CREDITS,
} from '../utils/billing-constants';
import { OPTIDEV_ADDON_KEY, optidevBasePrice } from '../utils/optidev-addon';
import { ProcessingOverlay } from '../components/ProcessingOverlay';
import { useAddOnPricing } from '@/features/screens/hooks/useAddOnPricing';
import { resolveAddonRequired } from '@/hooks/useSubscriptionModal';
import { usePermissions } from '@/hooks/usePermissions';

/** Device entry inside `dialogContext.items[].devices` or `dialogContext.devices`. */
type AddonDevice = {
  name?: string;
  _id?: string;
};

/** Shape of each element in `dialogContext.items` — backend returns one per addon that failed. */
type AddonItem = {
  key?: string;
  /** Backend-canonical addon key (matches `key` but used by some legacy payloads). */
  addOnKey?: string;
  devices?: AddonDevice[];
  /**
   * Exact license deficit computed by the backend — number of NEW licenses
   * needed (accounts for already-available unused licenses). Preferred over
   * `devices.length` because the devices array can include screens that
   * already have licenses covering them. Returned by `optisigns-open-api`
   * at device.service.ts:3575, 3616, 3915, 3955.
   */
  maxQuantityNeedIncrease?: number;
  /** Existing paid-license capacity (gates whether Add Licenses is shown — scio1 parity). */
  maxQuantity?: number;
  /** Backend error code for this specific addon (used by 2002 tier-mismatch branch). */
  code?: number;
  /** Whether this addon is on trial (gates 2002 footer button selection). */
  isTrial?: boolean;
  /** Whether the company itself is on a trial plan (different from addon trial). */
  isCompanyTrial?: boolean;
  /** Whether the company has a card on file. */
  haveCard?: boolean;
};

/**
 * Add-on required / activation modal (PW02).
 *
 * scio1 parity: header "Add-ons", lists affected devices. Branches on the
 * account's current add-on state:
 *   - No active license     → 3 actions (No / Add Licenses / Activate Trial)
 *                             with the 14-day trial pitch.
 *   - Already active        → 2 actions (No / Add Licenses) with the
 *                             "update your license" copy. Hides the trial
 *                             button because the trial is already consumed
 *                             or the account is past it.
 *
 * Opened by `useSubscriptionModal.openAddOnRequired` when an addon-license
 * error comes back from the backend (e.g. from `addWidgetAddonDevices`).
 * Context payload carries `addonType`, `devices`, `items`, `subAccountId`.
 */
export function AddonRequiredDialog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeDialog = useBillingDialogStore((s) => s.activeDialog);
  const dialogContext = useBillingDialogStore((s) => s.dialogContext);
  const closeDialog = useBillingDialogStore((s) => s.closeDialog);
  const apolloClient = useApolloClient();
  const setCompany = useAuthStore((s) => s.setCompany);

  const addonKey = (dialogContext.addonType as string) ?? '';
  const addonName = ADDON_DISPLAY_NAMES[addonKey] ?? addonKey;
  /**
   * Present only for a flat add-on the catalogue describes — App X today, absent everywhere else.
   *
   * It decides what the body SAYS, no longer where the button goes. The button charges here for
   * every add-on, so the package summary has to be here too: this is the last screen before the
   * card is billed, and a price with nothing beside it is not a thing anyone can agree to. Read
   * from the same catalogue entry the Subscription page renders, so the two cannot drift.
   */
  const flatPackage = addonKey ? ADDON_FLAT_PACKAGE[addonKey] : undefined;
  const subAccountId = dialogContext.subAccountId as string | undefined;
  /** The ADD-ON's plan floor (from its AppMeta), NOT the account's plan — see handleUpdateSubscription. */
  const addonMinPlanRequired = dialogContext.addonMinPlanRequired as string | undefined;
  // #1181 — SCIO1 parity (aericastAssignModal.js:73-82): when push hits
  // multiple addons (e.g. asset uses both AeriCast and VideoWall), the body
  // copy joins their names with ", ". Falls back to the single `addonName`
  // when items[] isn't populated. Built lazily later from `items`.

  const isOpen = activeDialog === 'addonRequired';

  // Resolve current add-on license state from billing data so we can branch
  // between the trial-pitch variant and the update-license variant.
  // Skip the query when the dialog is closed — useBillingData's auth-store
  // fallback is instant either way.
  const { addons, payment, planFeature, currency, currencySymbol } = useBillingData({
    subAccountId,
    skip: !isOpen,
  });
  /**
   * The period the account is actually billed on. Asked for below; the LABEL comes from what the
   * lookup resolved, never from this — see `getPriceInfoByKey`'s ⛔ and the #1478 revert it records.
   */
  const accountInterval =
    (payment as { plan?: string } | undefined)?.plan === 'yearly' ? 'yearly' : 'monthly';
  const thisAddon = addons.find((a) => a.key === addonKey);
  /**
   * The docblock's "already active" branch, in one flag.
   *
   * ⛔ NOT `items[0].maxQuantity`. That field is the licence capacity the ERROR PAYLOAD happened to
   * carry, and the already-active payload does not carry it — the account's own billing row does.
   * Reading the item would report "no licence" for an account holding two.
   */
  const holdsAddOn = thisAddon?.isActive === true;

  // Hoisted above the trial-availability logic below, which now consults the CATALOGUE first.
  const {
    isCatalogueLoaded,
    getPriceInfoByKey,
    getDaysByKey,
    getEnableTrialByKey,
    getIsDeviceManagedByKey,
    getIsAutoChargeEndTrialByKey,
    getHelpLinkTrialByKey,
    getNumberScreenTrialByKey,
  } = useAddOnPricing();

  // Trial availability — primary signal is the backend `isTrial` flag from the
  // exception payload (#1174). It directly reflects whether the user is currently
  // on trial / can start one (`true`) vs. has a paid add-on subscription (`false`).
  //
  // Backend overloads `codeStatus` 1000 for both "trial available" AND "paid sub,
  // no devices using add-on yet" — without consulting `isTrial`, a paid subscriber
  // sees an irrelevant Activate Trial button. Backend already includes `isTrial`
  // in the response (device.service.ts ~2192-2204); we just forward it through
  // useSubscriptionModal into dialogContext.
  //
  // Fallback chain when `isTrial` isn't present (legacy callers / tests):
  //   1. codeStatus 1000 (AERICAST_TRIAL) or 1006 (ADDON_ENABLE_TRIAL_NOT_ENOUGH)
  //      → scio1 parity (aericastAssignModal.html), kept for callers that only
  //      forward codeStatus.
  //   2. `!thisAddon?.isActive` — last-resort heuristic from local billing cache.
  const codeStatus = dialogContext.codeStatus as number | undefined;
  const isTrialFromContext = dialogContext.isTrial as boolean | undefined;

  // #1174 reopen — the backend `isTrial` flag is overloaded for both
  // "trial available to start" AND "trial currently running" (see
  // device.service.ts ~2192-2204). When the local billing cache shows
  // the addon subscription is already active and in trial state
  // (mirrors AddonList.tsx:288 `isInActiveTrial` pattern), force
  // Activate Trial CTA OFF. Falls through to the paid-subscriber path:
  // Add Licenses becomes primary, body copy switches to license-update.
  const isInActiveTrial = !!(thisAddon?.isTrial && thisAddon?.isActive);

  // #1618 (+ AeriCast / Background Music follow-up) — ADDON_NOT_ENOUGH (1001) is returned
  // ONLY when an add-on subscription already EXISTS, for EVERY add-on. Verified directly at
  // all three backend emit sites in device.service.ts (a "no sub" state never yields 1001):
  //
  //   ┌─────────────────────────┬──────────────────────────┬──────────────────────────────┐
  //   │ backend check            │ NO sub (trial offerable)  │ sub EXISTS, over quota        │
  //   ├─────────────────────────┼──────────────────────────┼──────────────────────────────┤
  //   │ checkEngageAddonReq ~1465│ 1005 / 1006               │ 1001 (line 1522)              │
  //   │ checkDeviceAssignAC ~2780│ 1006 (early return ~2869) │ 1001 (line 2897)              │
  //   │ generic feature loop~4360│ 1000 / 1005               │ 1001 (line 4428)              │
  //   └─────────────────────────┴──────────────────────────┴──────────────────────────────┘
  //
  // So a 1001 means the one-time trial was already started (or already converted to paid) —
  // the sub is either a paid sub (isTrial=false, offering a trial is nonsense) or a running
  // trial over quota (isTrial=true, can't "activate" a trial that's already active). In BOTH
  // sub-states "Activate Trial" must not be offered. This is a reliable BACKEND signal,
  // independent of the (possibly stale, right after activation) local `thisAddon` cache and
  // the overloaded `dialogContext.isTrial` flag that `isInActiveTrial` alone was missing —
  // which is why the button wrongly re-appeared for the remaining screens (and after a
  // declined "Add Licenses"). Originally scoped to `engage` out of caution; the scope is now
  // dropped because the semantics above are IDENTICAL for every add-on (AeriCast, Background
  // Music, Video Wall, OptiDev). This only affects code 1001 — it is orthogonal to #1174,
  // which disambiguates the overloaded code 1000 via the `isTrial` flag below (untouched).
  const trialAlreadyStarted = codeStatus === DEVICE_CODE_STATUS.ADDON_NOT_ENOUGH;

  // ⛔ The CATALOGUE has the final say. Every signal below is about whether this ACCOUNT could
  // start a trial; none of them know whether the add-on OFFERS one. xSocial ships with
  // `enableTrial: false`, so the paywall pitched "Activate 30-day Trial" for something
  // `startTrialAddOn` refuses outright — the customer clicks and gets an error.
  //
  // ⛔ AND it must have ARRIVED. Both catalogue reads answer `!== false`, so before the query
  // resolves they report the permissive shape — offers a trial, licensed per device — which is
  // exactly the wrong claim for xSocial. `useAddOnPricing` is `cache-and-network` with no skip and
  // nothing on the assets page (where this paywall opens) warms it, so that IS the first paint.
  // Same anti-flash reasoning as AddonUpdateDialog's `showQuantitySelector`.
  const addOnOffersTrial = isCatalogueLoaded && getEnableTrialByKey(addonKey);
  /**
   * Per-device licensing? False for account-flat add-ons — drives the pricing copy AND both CTA
   * labels below.
   *
   * ⛔ Guarded on arrival for the same reason as `addOnOffersTrial`, and biased the SAME way
   * `needsPaidPlanFirst` is: while the catalogue loads the lookup answers the permissive shape
   * (per-device), which would first-paint "Add Licenses" / "Manage Licenses" on an account-flat
   * add-on — a claim that is simply false, since one licence covers the account. `false` is the
   * safe side because "Get Add-on" is never WRONG for a per-device add-on, only less specific.
   */
  const isDeviceManaged = isCatalogueLoaded && getIsDeviceManagedByKey(addonKey);

  /**
   * ⛔ WHETHER "LICENCE" IS EVEN THE RIGHT NOUN — read by the buy button AND by both of its toasts,
   * so the label a customer clicks and the message they get back cannot describe different products.
   *
   * One licence covers the whole account, so an account-flat purchase adds no licences to anything:
   * "Licenses added successfully" after activating App X names a unit that does not exist in it.
   *
   * False until the catalogue lands, for the reason `isDeviceManaged` above is guarded: before it
   * does, EVERY add-on reads as account-flat, and the licence wording is the vaguer, safer side.
   */
  const licencesAreTheUnit = !isCatalogueLoaded || isDeviceManaged;

  /**
   * A TIERED add-on has no single price to quote here, so the copy below must not invent one.
   *
   * The customer has not picked a tier yet — with no add-on held, `thisAddon?.planFeature` is
   * undefined and the lookup silently resolves 'standard', presenting the cheaper of two packs as
   * though it were the price. (The interval half of this is fixed: the lookup is interval-aware now
   * and reports which period it resolved. The tier half is not fixable here — only the picker knows.)
   *
   * Same registry the tier cards render from, so the two can never disagree: an add-on that shows a
   * tier picker never gets a price in this sentence, and an untiered one (Branding) keeps the copy
   * it ships today, where the single price IS the whole truth.
   */
  const isTieredAddon = !!ADDON_TIER_CATALOG[addonKey];

  const isTrialAvailable =
    addOnOffersTrial &&
    !isInActiveTrial &&
    !trialAlreadyStarted &&
    (isTrialFromContext !== undefined
      ? isTrialFromContext
      : codeStatus !== undefined
        ? codeStatus === DEVICE_CODE_STATUS.AERICAST_TRIAL ||
          codeStatus === DEVICE_CODE_STATUS.ADDON_ENABLE_TRIAL_NOT_ENOUGH
        : !thisAddon?.isActive);

  // PW04 — Free plan + role gating (scio1 parity, aericastAssignModal.html:5/25/41/45).
  // Free-plan accounts cannot activate paid add-on trials; they must upgrade first.
  // Non-root admins on any plan can only Cancel — they don't have billing authority.
  const isFreePlan = planFeature === PLAN_FEATURES.FREE_PLAN;
  const { isRootAdmin } = usePermissions();

  // Per-addon price from the backend plan-features data — scio1 parity. Tier-aware (Pro vs
  // Standard), currency-aware (USD/EUR/GBP/…) and now interval-aware, so a yearly account is quoted
  // its yearly figure instead of the monthly one. Falls back to shipped defaults when the query
  // isn't ready.
  // #1181 #14 — pass current addon tier so multi-tier addons (BackgroundMusic Pro)
  // show their actual price instead of the Standard one.
  const priceInfo = getPriceInfoByKey(addonKey, {
    tier: thisAddon?.planFeature,
    currency,
    interval: accountInterval,
  });
  const priceText = priceInfo.text;
  // Read off what was RESOLVED — a catalogue with no yearly entry falls back to the monthly figure,
  // and this relabels with it rather than pairing a monthly number with "per year" (#1478).
  const billingIntervalText = priceInfo.interval === 'yearly' ? t('per year') : t('per month');
  /** The compact form, for the `{{price}}/{{interval}}/…` rate sentences. Same source, so it cannot
   *  drift from the long form or from the figure. */
  const billingIntervalShort = priceInfo.interval === 'yearly' ? t('yr') : t('mo');
  // #1181 — trial duration from planFeatures.add_ons[key].numberDayTrial (scio1
  // aericastAssignModal.html:20-21 fallback `|| 30`). Replaces hardcoded "14-day".
  const trialDays = getDaysByKey(addonKey);
  // #1181 #10 — V2/V3/V5 trial pitch selectors (scio1 .html:20-21).
  const isAutoChargeEndTrial = getIsAutoChargeEndTrialByKey(addonKey);
  const helpLinkTrial = getHelpLinkTrialByKey(addonKey);
  // #1181 #8 — OptiDev-specific trial copy (scio1 .html:12-17).
  const isOptiDev = addonKey === OPTIDEV_ADDON_KEY;
  const optiDevScreens = isOptiDev ? getNumberScreenTrialByKey(addonKey) : 0;

  // OptiDev is tier-priced, so the base package is Stripe's tier-1 flat amount —
  // NOT 3 × the per-screen overage. The two differ per currency (GBP is £25 base
  // against £8.35/screen), and the catalogue `price` map carries no tiers, so the
  // real base only reaches us through Stripe's currency_options. Same query and
  // shape AddonUpdateDialog uses; USD skips it and falls back to the derivation.
  const { data: currencyOptionsData } = useQuery<{
    getPriceAutoConvertCurrencies: Record<
      string,
      Record<string, Record<string, CurrencyOptionEntry>>
    >;
  }>(GET_PRICE_AUTO_CONVERT_CURRENCIES, {
    variables: { currency },
    skip: !isOpen || !isOptiDev || currency === 'usd',
  });
  const optidevPerScreen = parseFloat(priceText.replace(/^[^\d.]+/, '')) || 0;
  const optidevBase = optidevBasePrice(
    readAddonAmounts(
      currencyOptionsData?.getPriceAutoConvertCurrencies?.[OPTIDEV_ADDON_KEY]?.monthly?.[currency],
    ).baseFlat,
    optidevPerScreen,
  );

  // Prefer items[0].devices (structured backend payload from addWidgetAddonDevices error),
  // fall back to top-level devices (legacy/shim payloads). Memoized so
  // consumer useCallbacks (handleAddLicenses) have stable deps.
  const items = useMemo(
    () => (dialogContext.items as AddonItem[] | undefined) ?? [],
    [dialogContext.items],
  );
  const devicesFromItems = items[0]?.devices ?? [];
  const devicesFromContext = (dialogContext.devices as AddonDevice[] | undefined) ?? [];
  const allDevices = devicesFromItems.length > 0 ? devicesFromItems : devicesFromContext;
  // #1181 — SCIO1 parity (aericastAssignModal.js:44-52): for codeStatus 1001
  // (license shortfall) when maxQuantityNeedIncrease < devices.length, render
  // only the first N devices — those that actually need licenses. The rest are
  // already covered. Without this slice, scio3 listed every selected screen
  // including covered ones and confused users.
  const itemsNeedIncreaseForDisplay = items[0]?.maxQuantityNeedIncrease;
  const contextNeedIncreaseForDisplay = dialogContext.maxQuantityNeedIncrease as number | undefined;
  const needIncreaseForDisplay = contextNeedIncreaseForDisplay ?? itemsNeedIncreaseForDisplay;
  const devices = useMemo(() => {
    // #1618/#1619 — UNIFIED DISPLAY RULE: the Add-ons popup lists every SELECTED screen that
    // needs action. Activating the trial covers screens up to quota and buying converts the WHOLE
    // trial to paid, so with NO sub / a trial-available / an active-trial add-on, ALL selected
    // screens are involved → show them all (fixes "push 3 → popup shows 1"). Only when the add-on
    // already has an active PAID (non-trial) license do we trim to `maxQuantityNeedIncrease` — those
    // already-paid screens are genuinely covered and won't be re-charged (original #1181 intent).
    // Charge is UNAFFECTED either way: handleAddLicenses derives the buy quantity from
    // `maxQuantityNeedIncrease`, not devices.length, so listing all screens never over-charges.
    //
    // `maxQuantity > 0` guard: an add-on with zero paid seats has NO already-paid screens to hide, so
    // it must never trim. This also fixes the insufficient-funds case — a failed trial→paid buy can
    // leave the sub entry as {isActive:true, isTrial:false, maxQuantity:0}; without this guard the
    // popup wrongly dropped a screen (e.g. 3 → 2) after the failed charge. (BE state hygiene for that
    // rollback is tracked separately: ENGAGE-TRIAL-ROLLBACK-INSUFFICIENT-FUNDS.)
    const hasActivePaidLicense = !!(
      thisAddon?.isActive &&
      !thisAddon?.isTrial &&
      (thisAddon?.maxQuantity ?? 0) > 0
    );
    if (
      hasActivePaidLicense &&
      typeof needIncreaseForDisplay === 'number' &&
      needIncreaseForDisplay > 0 &&
      needIncreaseForDisplay < allDevices.length
    ) {
      return allDevices.slice(0, needIncreaseForDisplay);
    }
    return allDevices;
  }, [
    allDevices,
    needIncreaseForDisplay,
    thisAddon?.isActive,
    thisAddon?.isTrial,
    thisAddon?.maxQuantity,
  ]);

  const [startTrial, { loading: startingTrial }] = useMutation(START_TRIAL_ADDON, {
    refetchQueries: ['Me', 'GetCompanyInfo'],
  });
  const [updateAddon, { loading: addingLicenses }] = useMutation(UPDATE_SUBSCRIPTION_ADDON, {
    // SP47 — include GetDevicesAddOn so the subscription-plan "Licenses Used / Total"
    // updates after Add Licenses from the paywall flow.
    refetchQueries: ['Me', 'GetCompanyInfo', 'GetSubscriptionInfoStripe', 'GetDevicesAddOn'],
  });
  const isBusy = startingTrial || addingLicenses;

  // After a successful subscription mutation the auth-store `company` snapshot
  // (set at login by AuthProvider) is stale — the mutations' `refetchQueries`
  // update the Apollo cache but NOT the zustand store that `useBillingData` reads
  // for the MAIN account. Without this refresh, tier/license state (e.g.
  // `hasOptiSoundProLicense`) stays stale, so re-triggering the same gate right
  // after — e.g. picking another Pro Background Music station immediately after a
  // successful "Upgrade Plan" — wrongly re-opens this dialog (B14 class). Push a
  // fresh company into the store. Sub-accounts read fresh via a network-only
  // query already, so skip them.
  const refreshMainAccountCompany = useCallback(async () => {
    if (subAccountId) return;
    try {
      const res = await apolloClient.query({
        query: GET_COMPANY_INFO,
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      });
      const companyInfo = (res.data as { getCompanyInfo?: unknown } | undefined)?.getCompanyInfo;
      if (companyInfo) setCompany(companyInfo as Parameters<typeof setCompany>[0]);
    } catch {
      // non-fatal — refetchQueries + focus refetch reconcile it later
    }
  }, [subAccountId, apolloClient, setCompany]);

  const handleActivateTrial = useCallback(async () => {
    if (!addonKey) return;
    try {
      const result = await startTrial({
        variables: { addOn: { key: addonKey.toUpperCase() }, subAccountId },
      });
      const response = (
        result.data as
          | Record<string, { status?: boolean; message?: string } | undefined>
          | undefined
      )?.startTrialAddOn;
      if (response?.status) {
        // Refresh the auth-store company so a follow-up action sees the new
        // add-on state (B14 class) before we resolve the caller.
        await refreshMainAccountCompany();
        // Resolve the pending openAddOnRequired promise BEFORE closing so the
        // caller (e.g. AddOnConfigDialog) retries the original mutation.
        resolveAddonRequired({ status: true });
        toast(t('Trial started successfully'));
        closeDialog();
      } else {
        toast.error(response?.message ?? t('Failed to start trial'));
      }
    } catch {
      toast.error(t('Failed to start trial'));
    }
  }, [addonKey, startTrial, subAccountId, closeDialog, t, refreshMainAccountCompany]);

  const handleAddLicenses = useCallback(async () => {
    if (!addonKey) return;

    // #1181 — SCIO1 parity (aericastAssignModal.js:252-256): for codeStatus
    // 1002 (AERICAST_PAID_NOT_ENOUGH) the user already has paid licenses with
    // capacity for the new screens — `ok()` just closes with `status:true` and
    // the caller retries the original push with `forceAssignAC`. NO
    // editSubscriptionAddOn call. Without this guard, scio3 was bumping the
    // subscription's maxQuantity by `devices.length` and over-charging users
    // who already had spare licenses.
    if (codeStatus === DEVICE_CODE_STATUS.AERICAST_PAID_NOT_ENOUGH) {
      resolveAddonRequired({ status: true });
      closeDialog();
      return;
    }

    // scio1 parity: "Add Licenses" processes the purchase immediately using
    // the card on file — NO quantity popup. For multi-tier addons we
    // preserve the user's current tier (BackgroundMusic Standard stays
    // Standard; tier upgrades are a separate settings-page flow).
    //
    // License delta resolution (SP49): prefer the backend-computed shortage
    // at `items[0].maxQuantityNeedIncrease` over `devices.length`. When the
    // user already has unused licenses, `devices.length` includes screens
    // already covered → over-charges. `maxQuantityNeedIncrease` is the
    // exact NEW-license count needed. Matches scio1's
    // aericastAssignModal.ok() → `editSubscriptionAddOn({ number })`.
    const currentQuantity = thisAddon?.maxQuantity ?? 0;
    // Read `maxQuantityNeedIncrease` from both possible locations:
    //   - dialogContext top-level (set by openAddOnRequired when the exception
    //     ext carries it there — the AddOnConfigDialog path)
    //   - items[0].maxQuantityNeedIncrease (items-wrapper path — scio1 shape)
    // Fall back to `devices.length` only when neither is available.
    const contextNeedIncrease = dialogContext.maxQuantityNeedIncrease as number | undefined;
    const itemsNeedIncrease = items[0]?.maxQuantityNeedIncrease;
    const needIncrease = contextNeedIncrease ?? itemsNeedIncrease;
    const delta = needIncrease && needIncrease > 0 ? needIncrease : Math.max(devices.length, 1);
    const newQuantity = currentQuantity + delta;
    const currentTier = thisAddon?.planFeature;
    try {
      const result = await updateAddon({
        variables: {
          addOn: {
            key: addonKey.toUpperCase(),
            maxQuantity: newQuantity,
            ...(currentTier ? { selectedPlanTier: currentTier } : {}),
          },
          subAccountId,
        },
      });
      const response = extractMutationResponse<{
        status?: boolean;
        message?: string;
        code?: number;
      }>(result.data, 'updateSubscriptionAddOn');
      if (response?.status) {
        await refreshMainAccountCompany();
        // Resolve BEFORE closing so AddOnConfigDialog can retry the original
        // addWidgetAddonDevices mutation with the new license headroom.
        resolveAddonRequired({ status: true });
        toast.success(
          licencesAreTheUnit
            ? t('Licenses added successfully')
            : t('Add-on activated successfully'),
        );
        closeDialog();
      } else {
        toast.error(
          response?.message ??
            (licencesAreTheUnit ? t('Failed to add licenses') : t('Failed to activate add-on')),
        );
      }
    } catch {
      toast.error(
        licencesAreTheUnit ? t('Failed to add licenses') : t('Failed to activate add-on'),
      );
    }
  }, [
    addonKey,
    codeStatus,
    thisAddon,
    devices.length,
    items,
    dialogContext.maxQuantityNeedIncrease,
    subAccountId,
    updateAddon,
    closeDialog,
    t,
    refreshMainAccountCompany,
    licencesAreTheUnit,
  ]);

  // PW04 — "Update Subscription" path for Free-plan users (mirrors scio1
  // aericastAssignModal.openSubscriptionV2 → updateSubscription with
  // `minPlanRequired: PLAN_FEATURES.STANDARD`). Opens the wizard pre-set to
  // the Plan step (skipping Number-of-Screens) with the "Upgrade Required"
  // header and the plan list filtered to >= Standard. The wizard already
  // handles all of that automatically when `minPlanRequired` is in context
  // (see useSubscriptionWizard contextMinPlan + SubscriptionWizardDialog title).
  const handleUpgradeFromFree = useCallback(() => {
    resolveAddonRequired({ type: 'goToSub' });
    closeDialog();
    openUpgradeWizard(PLAN_FEATURES.STANDARD);
  }, [closeDialog]);

  // #1181 — "Manage Licenses" path (scio1 aericastAssignModal.goToSub, .js:209-215).
  // Closes the dialog and bubbles `{type: 'goToSub'}` to useSubscriptionModal's
  // handleToggleError, which navigates the user to /app/s/subscription-plan to
  // set up payment / manage existing licenses. Used when the user can't pay
  // in-place (no card on file, on company trial, multi-addon shortage).
  /**
   * "Update Subscription" for an account that cannot acquire the add-on yet (see
   * `needsPaidPlanFirst`) — opens the subscription wizard directly.
   *
   * ⛔ Keyed on the ADD-ON's plan floor, never the account's current plan. Using the current plan
   * filtered the wizard's cards to >= that tier, so a Pro Plus trialist was offered only Pro Plus
   * and Engage for an add-on whose floor is Standard. The floor also keeps this arm identical to the
   * Free-plan one below, which has always passed a requirement rather than a current plan.
   *
   * The upsell still survives: `minPlanPreSelect` pre-selects Pro Plus for any non-Engage minimum,
   * so Standard becomes *visible* without becoming the default.
   */
  const handleUpdateSubscription = useCallback(() => {
    closeDialog();
    openUpgradeWizard(addonMinPlanRequired || PLAN_FEATURES.STANDARD);
  }, [closeDialog, addonMinPlanRequired]);

  const handleManageLicenses = useCallback(() => {
    // `goToSub` is a REQUEST, not an action: the awaiting caller performs the navigation (see
    // AddOnConfigDialog / EditScreenBackgroundMusicSection / useSubscriptionModal's handleToggleError).
    //
    // ⛔ The app-lock entry point awaits nothing — `openBillingDialog('addonRequired')` from
    // useOpenAppModal has no promise behind it — so on that path the resolve was a no-op and the
    // button simply closed the dialog. Navigate ourselves only when nobody consumed the request, so
    // the legacy callers keep owning it and we never double-navigate.
    const consumed = resolveAddonRequired({ status: false, type: 'goToSub' });
    closeDialog();
    if (!consumed) navigate('/app/s/subscription-plan');
  }, [closeDialog, navigate]);

  // Tier-mismatch helpers — derived from items[0] for handleUpgradeTier.
  const firstItem = items[0];
  const firstItemIsTrial = firstItem?.isTrial ?? false;

  // #1181 — "Upgrade Plan" path for ADDON_TIER_MISMATCH (scio1
  // aericastAssignModal.js:257-274). Bumps the addon to the Pro tier via
  // editSubscriptionAddOn with `selectedPlanTier: PRO`. The `number` argument
  // mirrors scio1's special-case formula: 0 when (!isTrial && !needIncrease &&
  // currentTier !== PRO), otherwise needIncrease || devices.length.
  const handleUpgradeTier = useCallback(async () => {
    if (!addonKey) return;
    const currentTier = thisAddon?.planFeature;
    const currentQuantity = thisAddon?.maxQuantity ?? 0;
    // #1198 — `addWidgetAddonDevices` puts maxQuantityNeedIncrease at the TOP
    // level of the error payload, not inside items[0]. Without this
    // dialogContext fallback, paid users with a real license shortage
    // tripped isZeroNumberCase and we sent `maxQuantity: 0`, which the
    // backend interprets as a free tier swap (no Stripe charge) — silently
    // masking insufficient-funds failures and producing a false-success retry.
    const contextNeedIncrease = dialogContext.maxQuantityNeedIncrease as number | undefined;
    const itemNeedIncrease = contextNeedIncrease ?? firstItem?.maxQuantityNeedIncrease ?? 0;
    const itemDevicesCount = firstItem?.devices?.length ?? 0;
    // SCIO1 (.js:264) special-case: trial-not-on-tier with no shortage gets `number: 0`
    // (subscription-only tier change, no license adjustment).
    // #1198 — additionally require devices.length === 0 so a stale backend
    // `needIncrease: 0` (race during tier mismatch) can't trigger a tier-only
    // swap while devices are actively being pushed. Without this guard, a user
    // on Standard with N devices using BM gets flipped to Pro tier with 0
    // licenses, orphaning their existing devices in a weird limbo state.
    const isZeroNumberCase =
      !firstItemIsTrial &&
      !itemNeedIncrease &&
      itemDevicesCount === 0 &&
      currentTier !== PLAN_FEATURES.PRO;
    // #1198 — target = current + backend's reported shortage. When needIncrease
    // is 0 we keep the existing count (tier upgrade only — e.g. "2 Standard →
    // 2 Pro" when the pushed device is already among the 2). The Math.max(..., 1)
    // floor catches the post-downgrade race where currentQuantity=0 AND
    // needIncrease=0, ensuring we never send maxQuantity:0 as a tier-only swap.
    const newQuantity = isZeroNumberCase
      ? 0
      : Math.max(currentQuantity + Math.max(itemNeedIncrease, 0), 1);
    try {
      const result = await updateAddon({
        variables: {
          addOn: {
            key: addonKey.toUpperCase(),
            maxQuantity: newQuantity,
            selectedPlanTier: PLAN_FEATURES.PRO,
          },
          subAccountId,
        },
      });
      const response = extractMutationResponse<{
        status?: boolean;
        message?: string;
      }>(result.data, 'updateSubscriptionAddOn');
      if (response?.status) {
        // Critical for the BGM Pro flow: without refreshing the store, picking
        // another Pro station right after this upgrade re-opens this dialog
        // because `hasOptiSoundProLicense` is still read from the stale snapshot.
        await refreshMainAccountCompany();
        resolveAddonRequired({ status: true });
        toast.success(t('Plan upgraded successfully'));
        closeDialog();
      } else {
        toast.error(response?.message ?? t('Failed to upgrade plan'));
      }
    } catch {
      toast.error(t('Failed to upgrade plan'));
    }
  }, [
    addonKey,
    thisAddon,
    firstItem,
    firstItemIsTrial,
    dialogContext.maxQuantityNeedIncrease,
    subAccountId,
    updateAddon,
    closeDialog,
    t,
    refreshMainAccountCompany,
  ]);

  // #1181 — Add Licenses vs Manage Licenses visibility (scio1
  // aericastAssignModal.html:36-37). Show "Add Licenses" only when the user
  // can pay in-place (card on file + not on company trial, or already has
  // paid license capacity). Otherwise show "Manage Licenses" which routes
  // them to the subscription page to set up payment.
  const isCompanyTrial = (dialogContext.isCompanyTrial as boolean | undefined) ?? false;

  /**
   * The account cannot acquire this add-on YET — it is on a PLAN trial and the add-on offers no
   * add-on trial of its own, so neither route is open: `canActivateOnetime` requires
   * `!accountIsTrial && hasCard`, and `startTrialAddOn` refuses an `enableTrial: false` add-on.
   * They have to convert to a paid subscription first. Structurally the same dead end as Free plan.
   */
  // ⛔ Needed BECAUSE of the guard on `addOnOffersTrial` above, not independently of it: that guard
  // forces the flag false while the catalogue loads, so this would read true for EVERY trial account
  // in that window and flash "requires a paid subscription" at accounts it does not apply to.
  const needsPaidPlanFirst = isCatalogueLoaded && isCompanyTrial && !addOnOffersTrial;
  const haveCard = (dialogContext.haveCard as boolean | undefined) ?? false;
  const itemsAtMostOne = items.length <= 1;
  const firstItemMaxQuantity = items[0]?.maxQuantity ?? 0;

  const showAddLicenses = isTrialAvailable
    ? !isCompanyTrial
    : (itemsAtMostOne && haveCard && !isCompanyTrial) ||
      (firstItemMaxQuantity > 0 && itemsAtMostOne);
  const showManageLicenses = !showAddLicenses;

  // #1181 — ADDON_TIER_MISMATCH (codeStatus 2002, scio1
  // aericastAssignModal.html:40-45 + .js:257-274). Multi-tier addons
  // (BackgroundMusic Pro, etc.) hit this when an asset requires a higher
  // tier than the user's current addon subscription. Footer is split into
  // three actions:
  //   - Manage Licenses (if isCompanyTrial)
  //   - Upgrade Plan (if !isCompanyTrial && (isTrial || maxQuantityNeedIncrease <= 0))
  //   - Add Licenses (if !isCompanyTrial && !isTrial && maxQuantityNeedIncrease > 0)
  // All three submit `selectedPlanTier: PRO`.
  const isTierMismatch = codeStatus === DEVICE_CODE_STATUS.ADDON_TIER_MISMATCH;
  // #1198 — top-level fallback so the footer picks the right button
  // ("Add Licenses" with shortage > 0 vs "Upgrade Plan" for tier-only swap).
  const firstItemNeedIncrease =
    (dialogContext.maxQuantityNeedIncrease as number | undefined) ??
    firstItem?.maxQuantityNeedIncrease ??
    0;
  const tierShowManage = isTierMismatch && (firstItem?.isCompanyTrial ?? isCompanyTrial);
  const tierShowUpgradePlan =
    isTierMismatch &&
    !(firstItem?.isCompanyTrial ?? isCompanyTrial) &&
    (firstItemIsTrial || firstItemNeedIncrease <= 0);
  const tierShowAddLicenses =
    isTierMismatch &&
    !(firstItem?.isCompanyTrial ?? isCompanyTrial) &&
    !firstItemIsTrial &&
    firstItemNeedIncrease > 0;

  /**
   * With no device list to introduce, the generic "{{name}} add-on license required" line just
   * repeats the add-on name that the paragraph below already carries in a complete sentence —
   * "X Social add-on license required / X Social Add-on requires a paid subscription…".
   *
   * Scoped to the two ACCOUNT-FLAT branches so every device-managed add-on keeps the copy it
   * ships today.
   */
  const skipGenericHeader =
    devices.length === 0 &&
    !isTierMismatch &&
    !isTrialAvailable &&
    (needsPaidPlanFirst || !isDeviceManaged);

  // #1181 — Multi-addon name list (scio1 aericastAssignModal.js:73-82).
  // When push hits multiple addons, build "AeriCast, Video Wall" instead of
  // dropping the second one. Each item carries its `addOnKey`/`key`; map
  // through ADDON_DISPLAY_NAMES with a fallback to the bare key.
  const allAddonNames = useMemo(() => {
    if (items.length === 0) return addonName;
    const names = items
      .map((it) => {
        const key = it.addOnKey ?? it.key ?? '';
        return ADDON_DISPLAY_NAMES[key] ?? key;
      })
      .filter(Boolean);
    return names.length ? names.join(', ') : addonName;
  }, [items, addonName]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (open || isBusy) return;
        // Real close (ESC / outside click / No button). `activeDialog` would
        // still be 'addonRequired' here because none of our handlers reassign
        // it — Add Licenses now processes in-place.
        if (useBillingDialogStore.getState().activeDialog === 'addonRequired') {
          resolveAddonRequired({ close: true });
          closeDialog();
        }
      }}
    >
      <DialogContent
        className="max-h-[85vh] flex flex-col overflow-hidden sm:max-w-md"
        data-testid="addon-required-dialog"
        // Block ESC / outside-click / X while a mutation is in flight — the
        // ProcessingOverlay is the authoritative UX. DialogContent is already
        // `fixed`-positioned by Radix, so `absolute inset-0` inside the overlay
        // still anchors to the dialog box; no `relative` class needed (adding it
        // would conflict with `fixed` and break centering).
        hideCloseButton={isBusy}
        onEscapeKeyDown={(e) => {
          if (isBusy) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (isBusy) e.preventDefault();
        }}
      >
        <ProcessingOverlay visible={isBusy} />
        <DialogHeader>
          {/**
           * "Add-ons" names the SECTION of the billing page, not what this dialog asks. This is the
           * activation modal — the file's own docblock has called it that all along — so the header
           * names the decision.
           *
           * ⛔ EXCEPT WHEN THE ACCOUNT ALREADY HOLDS IT. That is the one state where "Activate" is
           * not merely vague but false: the body there reads "update your license … you currently
           * have 3 of 5", and the customer is topping up something already running. It is also the
           * exact split the docblock describes — "no active license" vs "already active" — and
           * `maxQuantity` is the existing paid-licence capacity that decides it, the same flag
           * `showAddLicenses` keys on.
           *
           * The refusal states (Free plan, trial-without-a-card, tier mismatch) deliberately keep
           * the activation header: the customer came here to activate, and the body's job is to say
           * why they cannot yet. A title that named the refusal would be a second, competing answer.
           */}
          <DialogTitle>{holdsAddOn ? t('Add-ons') : t('Activate Add-on')}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 py-2 text-sm">
          {isFreePlan ? (
            // PW04 — Free plan body (scio1 parity, aericastAssignModal.html:25-27)
            <p>{t('freePlanAddOnNotSupportError', { name: addonName })}</p>
          ) : (
            <>
              {/* #1181 — Multi-addon device list (scio1 aericastAssignModal.html:8).
                  Shows the affected screens with the comma-joined addon names so
                  users hitting AeriCast + VideoWall on the same asset see both. */}
              {devices.length > 0 ? (
                <>
                  <p>
                    {t('{{addonName}} add-on license required for:', { addonName: allAddonNames })}
                  </p>
                  <ul className="space-y-1 pl-2">
                    {devices.map((d, i) => (
                      <li key={d._id ?? i}>- {d.name ?? '—'}</li>
                    ))}
                  </ul>
                </>
              ) : skipGenericHeader ? null : (
                <p>{t('{{addonName}} add-on license required', { addonName: allAddonNames })}</p>
              )}

              {/* #1181 — Tier mismatch (codeStatus 2002) body copy.
                  scio1 aericastAssignModal.html:22-23: addonTierMismatch when
                  isTrial OR maxQuantityNeedIncrease<=0 (subscription-only upgrade);
                  otherwise the standard "Update license" copy applies. */}
              {isTierMismatch && (firstItemIsTrial || firstItemNeedIncrease <= 0) ? (
                <p>{t('addonTierMismatch', { text: allAddonNames })}</p>
              ) : !isTrialAvailable ? (
                /* #12 — non-trial pitch with totalLicense/numDevices counts
                   (scio1 aericastAssignScreen3 + aericastAssignScreen1). */
                items[0]?.maxQuantity != null && devices.length > 0 ? (
                  <p>
                    {t(
                      'Would you like to update your {{addonName}} add-on license? It will cost an additional {{price}}/{{interval}}/add-on screen. You currently have {{totalLicense}} of {{numDevices}} licenses needed.',
                      {
                        addonName: allAddonNames,
                        price: priceText,
                        interval: billingIntervalShort,
                        totalLicense: items[0].maxQuantity ?? 0,
                        numDevices: devices.length,
                      },
                    )}
                  </p>
                ) : /* ⛔ Every branch below reads the catalogue (`isDeviceManaged`, `isTieredAddon`,
                     `priceText`), and an absent catalogue reads as per-device — so rendering early
                     quotes a per-screen rate for an account-flat licence, then corrects itself.
                     Say nothing until it lands; the buttons below are unaffected. */
                !isCatalogueLoaded ? null : needsPaidPlanFirst ? (
                  /* A PLAN trial cannot buy an add-on — `canActivateOnetime` requires
                     `!accountIsTrial && hasCard`, and the backend refuses it too. Offering "$25/mo
                     for the whole account" here sells something that cannot be bought yet; the
                     account has to convert to paid first. Same shape as the Free-plan message. */
                  <p>
                    {t(
                      '{{name}} Add-on requires a paid subscription, please update your subscription.',
                      { name: addonName },
                    )}
                  </p>
                ) : !isDeviceManaged && isTieredAddon ? (
                  /* ACCOUNT-FLAT and TIERED (xSocial): licensed per account, and the tier picker one
                     click later quotes the exact price of each pack at the account's own interval.
                     No figure belongs in this sentence — see isTieredAddon. */
                  <p>
                    {t(
                      "Would you like to activate the {{addonName}} add-on? It will be charged per account — you'll choose your plan next.",
                      { addonName: allAddonNames },
                    )}
                  </p>
                ) : !isDeviceManaged ? (
                  /* ACCOUNT-FLAT, single tier (Branding, App X): one licence covers the whole
                     account, so there is NO per-screen rate and no screen suffix belongs here —
                     "/add-on screen" would quote a unit price that does not exist. With one tier the
                     quoted price IS the whole truth.

                     ⛔ "/account" ANSWERS A QUESTION NOBODY ASKED. A customer has exactly one
                     account, so a per-account denominator carries no information — while the thing
                     they are actually weighing is how many screens the money covers. Saying "covers
                     all your screens" states the same price as a benefit rather than as a unit.

                     Gated on the catalogue entry rather than applied to the whole branch, so
                     Branding keeps the "/account" sentence it was reviewed with — only its VERB
                     moves, so the body stops saying "add" under a header that says "Activate". */
                  flatPackage ? (
                    <>
                      <p>
                        {t(
                          'Would you like to activate the {{addonName}} add-on? A flat {{price}}/{{interval}} covers all your screens.',
                          {
                            addonName: allAddonNames,
                            price: priceText,
                            interval: billingIntervalShort,
                          },
                        )}
                      </p>
                      {/* WHAT THE MONEY BUYS, on the screen that spends it. The button below charges
                          the card on file with no further step, so the limits the customer is
                          agreeing to cannot live one navigation away. */}
                      {flatPackage.features.length > 0 && (
                        <ul
                          className="space-y-1 text-sm text-muted-foreground"
                          data-testid="addon-required-flat-features"
                        >
                          {flatPackage.features.map((key) => (
                            <li key={key} className="flex items-start gap-2">
                              <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                              <span>{t(key)}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {/* The dateless variant on purpose: this dialog never loads `nextCharge`, and
                          the rule is true without the date — a date we invented would not be. */}
                      {flatPackage.prorationNote && (
                        <p
                          className="text-xs text-muted-foreground"
                          data-testid="addon-required-flat-proration"
                        >
                          {t(flatPackage.prorationNote.key)}
                        </p>
                      )}
                    </>
                  ) : (
                    <p>
                      {t(
                        'Would you like to activate the {{addonName}} add-on? It will cost an additional {{price}}/{{interval}}/account.',
                        {
                          addonName: allAddonNames,
                          price: priceText,
                          interval: billingIntervalShort,
                        },
                      )}
                    </p>
                  )
                ) : (
                  <p>
                    {t(
                      'Would you like to update your {{addonName}} add-on license? It will cost an additional {{price}}/{{interval}}/add-on screen.',
                      {
                        addonName: allAddonNames,
                        price: priceText,
                        interval: billingIntervalShort,
                      },
                    )}
                  </p>
                )
              ) : isOptiDev ? (
                /* OptiDev leads with the credit allowance — the thing the money
                   actually buys — and states the licenses that come with it,
                   matching the add-on dialog and the subscription row. */
                <p>
                  {t(
                    'Start your {{days}}-day OptiDev trial? It includes {{credits}} AI credits every month and {{count}} screen licenses. After the trial it renews at {{price}}/{{interval}}.',
                    {
                      days: trialDays,
                      credits: OPTIDEV_BASE_CREDITS,
                      count: optiDevScreens,
                      // What renews is the base package; `priceText` is the
                      // per-screen rate and understated it threefold.
                      price: `${currencySymbol}${optidevBase.toFixed(2)}`,
                      // `optidevBase` is derived from the MONTHLY per-screen rate, not from
                      // `priceInfo`, so this stays "mo" on its own terms.
                      interval: t('mo'),
                    },
                  )}
                </p>
              ) : isAutoChargeEndTrial && helpLinkTrial ? (
                /* #10 V3 — autoCharge trial with help link. scio1 .html:21. */
                <p>
                  {t(
                    'Activate {{days}}-day Trial for {{name}} app? When trial is complete, each device using {{name}} will cost an additional {{price}} {{interval}}. Learn more.',
                    {
                      days: trialDays,
                      name: allAddonNames,
                      price: priceText,
                      interval: billingIntervalText,
                    },
                  )}{' '}
                  <a
                    href={helpLinkTrial}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline"
                  >
                    {t('Learn more')}
                  </a>
                </p>
              ) : (
                /* V2/V5 — generic X-day trial pitch. */
                <p>
                  {t(
                    'Activate {{days}}-day Trial for {{name}} app? When trial is complete, each device using {{name}} will cost an additional {{price}} {{interval}}.',
                    {
                      days: trialDays,
                      name: allAddonNames,
                      price: priceText,
                      interval: billingIntervalText,
                    },
                  )}
                </p>
              )}

              {/* #1181 — non-root-admin warning (scio1 aericastAssignModal.html:27-29).
                  Renders inside the paid-plan body so users without billing authority
                  see WHY the action buttons are hidden, not just an empty Cancel-only
                  footer. */}
              {!isRootAdmin && (
                <p className="text-center text-destructive">
                  <strong>{t('cannotUpgrade')}</strong>
                </p>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          {isFreePlan && isRootAdmin ? (
            // PW04 state #3 — Free plan + root admin: Cancel / Update Subscription
            // (scio1 aericastAssignModal.html:41-44, vm.openSubscriptionV2())
            <>
              <DialogClose asChild>
                <Button variant="ghost" className="cursor-pointer" disabled={isBusy}>
                  {t('Cancel')}
                </Button>
              </DialogClose>
              <Button onClick={handleUpgradeFromFree} disabled={isBusy} className="cursor-pointer">
                {t('Update Subscription')}
              </Button>
            </>
          ) : !isRootAdmin ? (
            // PW04 state #4 — non-root-admin: Cancel only (scio1 :45-47, applies to
            // Free OR paid — lower roles have no billing authority)
            <DialogClose asChild>
              <Button variant="ghost" className="cursor-pointer" disabled={isBusy}>
                {t('Cancel')}
              </Button>
            </DialogClose>
          ) : isTierMismatch ? (
            // #1181 — ADDON_TIER_MISMATCH (codeStatus 2002) footer (scio1
            // aericastAssignModal.html:40-45). Cancel + one of:
            //   - Manage Licenses (isCompanyTrial → goToSub)
            //   - Upgrade Plan (paid + (trial OR no shortage) → editAddon w/ Pro tier)
            //   - Add Licenses (paid + !trial + has shortage → editAddon w/ Pro tier)
            <>
              <DialogClose asChild>
                <Button variant="ghost" className="cursor-pointer" disabled={isBusy}>
                  {t('Cancel')}
                </Button>
              </DialogClose>
              {tierShowManage && (
                <Button onClick={handleManageLicenses} disabled={isBusy} className="cursor-pointer">
                  {t('Manage Licenses')}
                </Button>
              )}
              {tierShowUpgradePlan && (
                <Button onClick={handleUpgradeTier} disabled={isBusy} className="cursor-pointer">
                  {t('Upgrade Plan')}
                </Button>
              )}
              {tierShowAddLicenses && (
                <Button onClick={handleUpgradeTier} disabled={isBusy} className="cursor-pointer">
                  {t('Add Licenses')}
                </Button>
              )}
            </>
          ) : (
            // Default — paid plan + root admin: No / [Manage Licenses | Add Licenses] / [Activate Trial]
            // SCIO1 parity (aericastAssignModal.html:34-38). Manage and Add Licenses are
            // mutually exclusive: Add shows when user can pay in-place (card on file +
            // not on company trial, OR has paid license capacity); Manage shows
            // otherwise and routes to /app/s/subscription-plan via 'goToSub'.
            <>
              <DialogClose asChild>
                <Button
                  variant="ghost"
                  className="cursor-pointer"
                  disabled={isBusy}
                  data-testid="addon-required-no-btn"
                >
                  {t('No')}
                </Button>
              </DialogClose>
              {showManageLicenses && (
                <Button
                  data-testid="addon-required-manage-btn"
                  // DESIGN_SYSTEM: the filled variant carries the MAIN action; outline is the
                  // secondary one. The only thing that can outrank this button is Activate Trial,
                  // which renders beside it when a trial is on offer — so demote to outline exactly
                  // then, and lead otherwise. Same expression the sibling showAddLicenses button
                  // uses, so the two can never disagree about which is primary.
                  //
                  // Keyed on `needsPaidPlanFirst` before, which left Get Add-on outline for a PAID
                  // account with nothing competing: the dialog's one real action, drawn as if it
                  // were secondary, next to a ghost "No".
                  variant={isTrialAvailable ? 'outline' : 'default'}
                  onClick={needsPaidPlanFirst ? handleUpdateSubscription : handleManageLicenses}
                  disabled={isBusy}
                  className="cursor-pointer"
                >
                  {/* An ACCOUNT-FLAT add-on has no licences to manage — one covers the account, and
                      the customer does not own it yet. The destination is right (the subscription
                      page, where the Subscribe button for this add-on lives); only the promise was
                      wrong. */}
                  {needsPaidPlanFirst
                    ? t('Update Subscription')
                    : isDeviceManaged
                      ? t('Manage Licenses')
                      : t('Get Add-on')}
                </Button>
              )}
              {showAddLicenses && (
                <Button
                  data-testid="addon-required-buy-btn"
                  variant={isTrialAvailable ? 'outline' : 'default'}
                  /**
                   * ⛔ EVERY ADD-ON BUYS HERE, INCLUDING A FLAT PACKAGE.
                   *
                   * This briefly routed a flat package to the Subscription page instead, so the
                   * customer could read the full card before paying. It cost more than it bought:
                   * the paywall fires mid-task, so leaving — in this tab or another — stranded the
                   * half-configured app behind it. Buying in place keeps their work.
                   *
                   * ⚠ It does NOT yet unlock what they bought. A manifest gates its paid fields on a
                   * value stamped by the app's own `onLoad` when the dialog opened, and refreshing
                   * the company does not restamp it — so the field stays locked until a reload. The
                   * fix belongs in the gate (reading the account rather than a snapshot of it), not
                   * here.
                   *
                   * What the routing was protecting is the review step, so it moved into this
                   * dialog — see the package summary in the body. The interval was never a choice
                   * here: an add-on bills on the account's own interval either way.
                   */
                  onClick={handleAddLicenses}
                  disabled={isBusy}
                  className="cursor-pointer"
                >
                  {/*
                    THE BUTTON SAYS WHAT THE TITLE SAYS — a footer reading "Get Add-on" under a
                    header reading "Activate Add-on", above a body asking "Would you like to
                    activate…", was three names for one click. `holdsAddOn` is the title's own flag,
                    so the two agree on the one case where the account already has it.

                    "Get" also described a DESTINATION, which is what this button used to be. It
                    charges the card now, so the label has to name the act, not the journey.

                    ⛔ AND IT STAYS NEUTRAL UNTIL THE LICENCE MODEL IS KNOWN. `isDeviceManaged` is
                    `isCatalogueLoaded && …`, so every add-on — including the five per-screen ones —
                    reads as account-flat on the first paint. "Activate Add-on" there would make a
                    specific promise about a model we have not loaded, then flip to "Add Licenses";
                    "Get Add-on" is the vaguer word, and vague is what this moment actually knows.
                  */}
                  {licencesAreTheUnit
                    ? isCatalogueLoaded
                      ? t('Add Licenses')
                      : t('Get Add-on')
                    : holdsAddOn
                      ? t('Get Add-on')
                      : t('Activate Add-on')}
                </Button>
              )}
              {isTrialAvailable && (
                <Button
                  data-testid="addon-required-trial-btn"
                  onClick={handleActivateTrial}
                  disabled={isBusy}
                  className="cursor-pointer"
                >
                  {t('Activate Trial')}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
